<script setup>
import { isEqual } from 'lodash'
import { ref, watch } from 'vue'

import {
  CONFIGURABLE_PROGRESS_MODULE_COLUMNS,
  DEFAULT_ENABLED_PROGRESS_MODULE_COLUMNS,
  PROGRESS_MODULE_COLUMN_IDS
} from '@/utils/custom-dashboard-helper'
import { handleError } from '@/utils/error-handling'
import {
  progressModuleConfigurableColumnsPropValidator,
  numberOrNullProp,
  stringOrNullProp
} from '@/utils/prop-validators'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import AppButton from '@/components/ui/AppButton/AppButton'
import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import Modal from '@/components/ui/Modal/Modal'

defineOptions({
  name: 'ModuleColumnsSettingsModal',
  inheritAttrs: false
})

const props = defineProps({
  enabledColumns: {
    type: Array,
    default: () => [],
    validator: v => progressModuleConfigurableColumnsPropValidator(v)
  },

  moduleId: {
    required: true,
    validator: v => numberOrNullProp(v) || stringOrNullProp(v)
  }
})

const emit = defineEmits({
  close: null,
  'update-enabled-columns': null
})

const localEnabledColumns = ref([])

watch(
  () => props.enabledColumns,
  newValue => {
    localEnabledColumns.value = [...newValue]
  },
  { immediate: true }
)

const restoreDefaults = () => {
  localEnabledColumns.value = [...DEFAULT_ENABLED_PROGRESS_MODULE_COLUMNS]
}

const isLoading = ref(false)

const save = async () => {
  if (isLoading.value) return

  if (isEqual(localEnabledColumns.value, props.enabledColumns)) {
    emit('close')
    return
  }

  try {
    isLoading.value = true
    emit('update-enabled-columns', {
      moduleId: props.moduleId,
      enabledColumns: localEnabledColumns.value
    })
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const COLUMN_NAMES = {
  [PROGRESS_MODULE_COLUMN_IDS[TABLE_COLUMNS_KEYS.OWNER]]: 'objectives.table_header_assignee',
  [PROGRESS_MODULE_COLUMN_IDS[TABLE_COLUMNS_KEYS.GROUPS]]: 'objectives.table_header_groups',
  [PROGRESS_MODULE_COLUMN_IDS[TABLE_COLUMNS_KEYS.CHECKIN]]: 'objectives.table_header_checkin'
}
</script>

<template>
  <Modal
    :scrollable-wrapper="false"
    :show="Boolean(moduleId)"
    manual-close
    scrollable-content
    size="xs"
    style="--modal-header-padding: 18px 20px 18px 20px"
    @close="emit('close')"
  >
    <template #header>
      <AppTitle :level="3" disable-margin>
        {{ $t('objective.columns_settings.title') }}
      </AppTitle>
    </template>

    <template #modal-body>
      <div class="mcsm-Content">
        <div class="mcsm-Options">
          <div
            v-for="column in CONFIGURABLE_PROGRESS_MODULE_COLUMNS"
            :key="column"
            class="mcms-Options_Item"
          >
            <AppCheckbox
              :model-value="localEnabledColumns"
              :value="column"
              full-height
              @update:model-value="localEnabledColumns = [...$event]"
            >
              {{ $t(COLUMN_NAMES[column]) }}
            </AppCheckbox>
          </div>
        </div>
        <div class="mcsm-Footer">
          <AppButton
            class="mcsm-Footer_Restore"
            icon="restore"
            remove-padding
            type="link-next"
            @click="restoreDefaults"
          >
            {{ $t('action.restore') }}
          </AppButton>

          <AppButton :loading="isLoading" type="primary-next" @click="save">
            {{ $t('action.done') }}
          </AppButton>
        </div>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/table-modals-helpers';

.mcsm-Content {
  @extend %columns-settings-modal-content;
}

.mcsm-Options {
  @extend %columns-settings-modal-columns-list;
}

.mcms-Options_Item {
  @extend %columns-settings-modal-columns-list-item;
}

.mcsm-Footer {
  @extend %columns-settings-modal-footer;

  &_Restore {
    color: $dark-3;
  }
}
</style>
